"use client"

import { PDFReader } from "./pdf-reader"
import { SubscriptionPrompt } from "./subscription-prompt"
import { useState, useEffect } from "react"
import { useSession } from "next-auth/react"
import { useRouter } from "next/navigation"
import { SessionManager, useSessionManager } from "@/lib/session-manager"
import { isFreeMode } from "@/lib/feature-flags"

interface ProtectedPDFReaderProps {
  pdfUrl: string
  title: string
  onClose: () => void
  bookSlug?: string
}

export function ProtectedPDFReader({ pdfUrl, title, onClose, bookSlug }: ProtectedPDFReaderProps) {
  const { data: session, status } = useSession()
  const router = useRouter()
  const sessionManager = SessionManager.getInstance()
  const { handleSessionExpired } = useSessionManager()
  
  const [showSubscriptionPrompt, setShowSubscriptionPrompt] = useState(false)
  const [isAccessLimitError, setIsAccessLimitError] = useState(false)
  const [isSessionExpired, setIsSessionExpired] = useState(false)

  useEffect(() => {
    if (status === 'loading' || isFreeMode) return

    // Check if this is a new book access that should trigger subscription prompt
    const checkAccess = async () => {
      if (!session?.user || !(session.user as { id: string }).id) {
        router.push(`/auth/signin?callbackUrl=${encodeURIComponent(window.location.pathname)}`)
        return
      }

      try {
        const response = await fetch(pdfUrl, { 
          method: 'HEAD',
          cache: "no-store"
        })
        
        if (response.status === 401) {
          // Session expired
          setIsSessionExpired(true)
          await handleSessionExpired()
          return
        }
        
        if (response.status === 403) {
          setIsAccessLimitError(true)
          setShowSubscriptionPrompt(true)
        }
      } catch (error) {
        // Handle fetch errors
        const sessionError = await sessionManager.handleFetchError(error as Error)
        if (sessionError.isSessionExpired) {
          setIsSessionExpired(true)
          await handleSessionExpired()
        }
        console.error('Error checking access:', error)
      }
    }

    checkAccess()
  }, [pdfUrl, session, status, router, sessionManager, handleSessionExpired])

  if (isSessionExpired) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
        <div className="bg-background border-0 shadow-2xl rounded-lg overflow-hidden w-[92vw] h-[92vh] lg:w-[85vw] lg:h-[85vh] flex items-center justify-center">
          <div className="text-center p-8">
            <div className="text-6xl mb-4">🔐</div>
            <h3 className="text-xl font-semibold mb-2">Session Expired</h3>
            <p className="text-muted-foreground mb-4">
              Your session has expired. Redirecting you to the login page...
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!isFreeMode && isAccessLimitError) {
    return (
      <>
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
          <div className="bg-background border-0 shadow-2xl rounded-lg overflow-hidden w-[92vw] h-[92vh] lg:w-[85vw] lg:h-[85vh] flex items-center justify-center">
            <div className="text-center p-8">
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-xl font-semibold mb-2">Access Limit Reached</h3>
              <p className="text-muted-foreground mb-4">
                You&apos;ve reached your free tier limit of 5 books. Upgrade to Premium for unlimited access.
              </p>
            </div>
          </div>
        </div>
        <SubscriptionPrompt
          isOpen={showSubscriptionPrompt}
          onClose={() => setShowSubscriptionPrompt(false)}
          title="Upgrade to Premium"
          message="You've reached your free tier limit"
        />
      </>
    )
  }

  return <PDFReader pdfUrl={pdfUrl} title={title} onClose={onClose} bookSlug={bookSlug} />
}
